export type ApiErrorInfo = {
  message: string;
  originalError?: unknown;
  statusCode?: number;
  type: ApiErrorType;
};

export type ApiErrorType = "authentication" | "network" | "server" | "unknown" | "validation";

export const handleApiError = (err: unknown, defaultMessage = "An error occurred"): string => {
  const errorInfo = categorizeApiError(err, defaultMessage);

  console.error("[API Error]", {
    message: errorInfo.message,
    statusCode: errorInfo.statusCode,
    timestamp: new Date().toISOString(),
    type: errorInfo.type,
    ...(import.meta.env.DEV && { fullError: err }),
  });

  return errorInfo.message;
};

const handleHttpStatusError = (
  status: number,
  serverMessage: string | undefined,
  err: unknown,
): ApiErrorInfo | null => {
  if (status === 401) {
    return {
      message: serverMessage ?? "Authentication required. Please log in again.",
      originalError: err,
      statusCode: status,
      type: "authentication",
    };
  }

  if (status === 403) {
    return {
      message: serverMessage ?? "Access denied. You don't have permission for this action.",
      originalError: err,
      statusCode: status,
      type: "authentication",
    };
  }

  if (status === 429) {
    return {
      message: serverMessage ?? "Too many requests. Please wait a moment and try again.",
      originalError: err,
      statusCode: status,
      type: "validation",
    };
  }

  if (status === 503) {
    const isRateLimited =
      serverMessage?.toLowerCase().includes("rate limit") ||
      serverMessage?.toLowerCase().includes("too many");

    return {
      message:
        serverMessage ??
        (isRateLimited
          ? "Service temporarily unavailable. Please wait a moment and try again."
          : "Service temporarily unavailable. Please try again later."),
      originalError: err,
      statusCode: status,
      type: isRateLimited ? "validation" : "server",
    };
  }

  if (status >= 400 && status < 500) {
    return {
      message: serverMessage ?? "Invalid request. Please check your input.",
      originalError: err,
      statusCode: status,
      type: "validation",
    };
  }

  if (status >= 500) {
    return {
      message: serverMessage ?? "Server error. Please try again later.",
      originalError: err,
      statusCode: status,
      type: "server",
    };
  }

  return null;
};

const isNetworkError = (err: unknown): boolean => {
  if (!err || typeof err !== "object" || !("code" in err)) {
    return false;
  }
  const networkError = err as { code?: string };
  return networkError.code === "NETWORK_ERROR" || networkError.code === "ECONNREFUSED";
};

export const categorizeApiError = (
  err: unknown,
  defaultMessage = "An error occurred",
): ApiErrorInfo => {
  if (err && typeof err === "object" && "response" in err) {
    const axiosError = err as {
      message?: string;
      response?: {
        data?: { message?: string };
        status?: number;
      };
    };

    const status = axiosError.response?.status;
    if (status) {
      const result = handleHttpStatusError(status, axiosError.response?.data?.message, err);
      if (result) return result;
    }
  }

  if (isNetworkError(err)) {
    return {
      message: "Network connection failed. Please check your internet connection.",
      originalError: err,
      type: "network",
    };
  }

  if (err instanceof Error) {
    return {
      message: err.message ?? defaultMessage,
      originalError: err,
      type: "unknown",
    };
  }

  return {
    message: defaultMessage,
    originalError: err,
    type: "unknown",
  };
};
